import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, User, ArrowRight, BookOpen } from 'lucide-react';
import { blogPosts, blogCategories } from '../data/blog';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white text-cyan-600 mb-6">
            <BookOpen className="h-4 w-4 mr-2" />
            Knowledge Center
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Water Health Blog</h1>
          <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
            Expert insights on water quality, health benefits, and wellness from our team of specialists
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {blogCategories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? "default" : "outline"}
                className={selectedCategory === category 
                  ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-white" 
                  : "border-2 border-gray-300"}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Card 
                key={post.id}
                className="overflow-hidden border-2 hover:border-cyan-400 transition-all duration-300 hover:shadow-xl group flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <Badge className="absolute top-4 right-4 bg-white text-cyan-600 border-0">
                    {post.category}
                  </Badge>
                </div>

                <CardHeader className="flex-grow">
                  <CardTitle className="text-xl leading-tight group-hover:text-cyan-600 transition-colors">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="text-base line-clamp-3">
                    {post.excerpt}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>

                  <Link to={`/blog/${post.slug}`}>
                    <Button 
                      variant="link" 
                      className="text-cyan-600 p-0 h-auto font-semibold group-hover:translate-x-2 transition-transform"
                    >
                      Read Full Article
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Want to Experience These Benefits?
          </h2>
          <p className="text-lg mb-8 text-cyan-50">
            Get your Niloman HX Smart RO system and start your journey to optimal health
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = '/shop'}
              size="lg" 
              className="bg-white text-cyan-600 hover:bg-gray-100"
            >
              Shop Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => window.location.href = '/contact'}
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/10"
            >
              Book Free Demo
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
